import { CONTENT_ALIGNMENT } from '@pperform/pp-constants/widgetConfigs';
import PpTrackingEventDateTime from '@pperform/pp-react-components/PpTrackingEventDateTime';
import { hasZeroTime } from '@pperform/pp-utils/datetime.utils';
import { MixedEvent, ParcelEvent } from '@pperform/pp-data-mappers';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { useTranslation } from 'react-i18next';
import {
  Event,
  EventDetails,
  EventLocation,
  EventMain,
  EventName,
  EventNameLabel,
  EventTime,
  TrackingTime,
  VisibilityIcon,
} from '../TrackingTimeline.styles';
import { EventIssue } from './EventIssue';
import { getHighlightStatus } from '../TrackingTimeline.service';
import { EventInfo } from './EventInfo';

interface EventRowProps {
  event: MixedEvent | ParcelEvent;
  isLatestEvent: boolean;
}

export const EventRow = ({
  event,
  isLatestEvent,
}: EventRowProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    timezone,
    trackingDetailsBox: {
      eventTimeFormat,
      timelineAlignment,
      highlightEventsType,
      shouldHideEventLocations,
    },
  } = usePTPConfigContext();

  // Hide time part if carrier does not provide time
  // Ref: https://www.pivotaltracker.com/story/show/174420589
  const timeFormat = hasZeroTime(event.eventTime) ? '-' : eventTimeFormat;
  const isHiddenEvent = event.eventTypeMasterData?.isConsumerVisible === false;
  const isHighlighted = getHighlightStatus(isLatestEvent, highlightEventsType);

  const id = `tracking-details-event-${event.id}`;

  return (
    <Event data-testid={id}>
      <EventTime>
        <TrackingTime data-testid={`${id}-time`}>
          <PpTrackingEventDateTime
            time={event.eventTime}
            parsedEventTimeTimezone={event.parsedEventTimeTimezone}
            timeFormat={timeFormat}
            userTimezone={timezone}
            isShownTime
            isShownDate={false}
            isShownInfo={false}
          />
        </TrackingTime>
      </EventTime>
      <EventDetails>
        <EventMain alignment={timelineAlignment ?? CONTENT_ALIGNMENT.CENTER}>
          <EventName>
            <EventNameLabel
              data-testid={`${id}-displayName`}
              isHiddenEvent={isHiddenEvent}
              isHighlighted={isHighlighted}
            >
              {t(event.eventTypeMasterData?.key) || event.eventType}
            </EventNameLabel>

            {isHiddenEvent && (
              <VisibilityIcon
                role="button"
                aria-label={t('ALT_TEXT_VISIBILITY_OFF_ICON')}
                data-testid={`visibility-icon-${event.id}`}
              />
            )}

            <EventInfo event={event} />
            <EventIssue event={event} />
          </EventName>

          {!shouldHideEventLocations && (
            <EventLocation
              data-testid={`${id}-location`}
              alignment={timelineAlignment ?? CONTENT_ALIGNMENT.CENTER}
            >
              {event.eventLocation}
            </EventLocation>
          )}
        </EventMain>
      </EventDetails>
    </Event>
  );
};
